// Vue
import { computed, defineComponent, PropType } from "vue";

// Providers
import { useLocalization } from "@/providers/Localization";
import { useClassificationTypes } from "@/dataProviders/ClassificationTypes";
import type { ClassificationTypesType } from "@/dataProviders/ClassificationTypes";

// Types
import type { BuildingProjectType, NewBuildingProjectType } from "@/features/BuildingProject";

// Utils
import {
  defaultClassificationParameter,
  resolveClassificationDefaults,
} from "@/features/BuildingProject/utils/classificationDefaults";

// Components
import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";

const ParameterMapping = defineComponent({
  props: {
    project: {
      type: Object as PropType<BuildingProjectType | NewBuildingProjectType>,
      required: true,
    },
  },
  setup(props) {
    // ///////////////////
    // Providers
    // ///////////////////

    const localization = useLocalization();
    const classificationTypes = useClassificationTypes();

    // ///////////////////
    // Computed
    // ///////////////////

    /**
     * What the two selects show: the project's own classification where it has one, the country's
     * defaults where it does not.
     *
     * Shown, never written. This card used to write the defaults into the project from `onMounted`,
     * which on the settings page runs after the page has taken the baseline its "edited" markers
     * compare against — so a freshly created project reported "1 section edited, not saved yet" and a
     * green complete check on a Classification section nobody had been near (RTLCA-5782). It was also a
     * race against the async query that fills the store: too early and it wrote nothing and left both
     * selects blank, and it never ran again, so a discard left them blank too.
     *
     * A computed has neither problem — it shows the default the moment the types arrive, on every
     * render, without touching the project. See `classificationDefaults.ts` for why an unchosen default
     * must stay out of the saved payload.
     */
    const effectiveClassification = computed(() =>
      resolveClassificationDefaults(classificationTypes.getClassificationTypes(), props.project?.speckleConfiguration)
    );

    // ///////////////////
    // Methods
    // ///////////////////

    /**
     * The only writer, and it always writes both fields: a stored row naming a parameter without the
     * system it belongs to means nothing, and the two selects are one answer.
     */
    function writeParameters(classificationTypeId: string, classificationCode: string) {
      props.project.speckleConfiguration = {
        ...props.project.speckleConfiguration,
        parameters: { classificationTypeId, classificationCode },
      };
    }

    function updateClassificationCode(value: string) {
      // The system travels with it. While it is merely DEFAULTED, picking a parameter is the reader's
      // first choice on this card, and the row has to record which system that parameter belongs to.
      writeParameters(effectiveClassification.value.classificationTypeId, value);
    }

    function updateClassificationType(value: string) {
      // A parameter belongs to a system, so changing the system moves the parameter to that system's
      // own default — or to nothing, when it nominates none. Keeping the previous system's parameter
      // would leave the select showing a value that is not among its options.
      writeParameters(value, defaultClassificationParameter(classificationTypes.getClassificationTypes(), value));
    }

    // ///////////////////
    // Return
    // ///////////////////

    return {
      props,
      localization,
      classificationTypes,
      effectiveClassification,
      updateClassificationCode,
      updateClassificationType,
    };
  },
  render() {
    const { classificationTypes, effectiveClassification, updateClassificationCode, updateClassificationType } = this;

    return (
      <div class="grid grid-cols-12">
        {/* The column headings are the selects' real labels, so each select has an accessible name
            (axe select-name failed on the bare selects the old header divs sat above). */}
        <div class="col-span-5">
          <SelectInputWithKeyValue
            {...{
              label: "Classification system",
              options: classificationTypes.getClassificationTypes().map((classificationType: ClassificationTypesType[0]) => ({
                name: classificationType?.classificationType?.shortName,
                value: classificationType?.classificationType?.id,
              })),
              placeholder: "Select classification system",
              value: effectiveClassification.classificationTypeId,
              "onUpdate:value": (event: any) => {
                // Update the classification type in the project
                updateClassificationType(event);
              },
            }}
          />
        </div>

        <div class="col-span-2 flex h-9 items-center justify-center self-end text-center">
          <span> = </span>
        </div>
        <div class="col-span-5">
          <SelectInputWithKeyValue
            {...{
              label: "Parameter",
              // The DEFAULTED system counts: the parameter select was disabled whenever the project
              // stored no system of its own, which after the mount write went away would have left it
              // permanently dead on every project that had never chosen one.
              isDisabled: !effectiveClassification.classificationTypeId,
              options: classificationTypes
                .getClassificationParametersFromTypeId(effectiveClassification.classificationTypeId)
                ?.map((parameter) => ({
                  name: parameter.value,
                  value: parameter.value,
                })),
              placeholder: "Select parameter",
              value: effectiveClassification.classificationCode,
              "onUpdate:value": (event: any) => {
                // Update the classification code in the project
                updateClassificationCode(event);
              },
            }}
          />
        </div>
      </div>
    );
  },
});

export default ParameterMapping;
